import type { BadgeTone } from '@vnetwork/datacentral-ui/primitives'

export interface SearchResult {
    type: SearchResultType
    ref_id: string
    service_id?: string
    customer_id?: string
    company_name?: string
    entity_name?: string
    email?: string
    industry?: string
    services?: string
    details?: string
    service_name?: string
    contract_id?: number
    contract_number_id?: string
    invoice_code?: string
    account_manager?: string
    activated_at?: string
    created_at: string
    customer_status?: CustomerStatus
    service_status?: ServiceStatus
    invoice_status?: InvoiceStatus
    contract_status?: ContractStatus
}

export type SearchResultType = 'customer' | 'service' | 'invoice' | 'contract'

export type CustomerStatus =
    | 'trial'
    | 'cool-lead'
    | 'qualified-prospect'
    | 'stopped'
    | 'active'
    | 'dead-lead'

export const CUSTOMER_STATUS_CONFIG: Record<
    CustomerStatus,
    { label: string; tone: BadgeTone }
> = {
    trial: { label: 'customer-trial', tone: 'warning' },
    'cool-lead': { label: 'cool-lead', tone: 'neutral' },
    'qualified-prospect': { label: 'qualified-prospect', tone: 'primary' },
    stopped: { label: 'stopped', tone: 'danger' },
    active: { label: 'customer-active', tone: 'success' },
    'dead-lead': { label: 'dead-lead', tone: 'danger' },
}

export type ServiceStatus =
    | 'waiting-confirm'
    | 'active'
    | 'cancelled'
    | 'trialing'
    | 'rejected'
    | 'on-resubmit-information'
    | 'waiting-create-service'
    | 'pending-terminated'

export const SERVICE_STATUS_CONFIG: Record<
    ServiceStatus,
    { label: string; tone: BadgeTone }
> = {
    'waiting-confirm': { label: 'waiting-confirm', tone: 'primary' },
    active: { label: 'active', tone: 'success' },
    cancelled: { label: 'cancelled', tone: 'danger' },
    trialing: { label: 'trialing', tone: 'warning' },
    rejected: { label: 'rejected', tone: 'danger' },
    'on-resubmit-information': {
        label: 'on-resubmit-information',
        tone: 'warning',
    },
    'waiting-create-service': {
        label: 'waiting-create-service',
        tone: 'warning',
    },
    'pending-terminated': {
        label: 'pending-terminated',
        tone: 'warning',
    },
}

export type InvoiceStatus = 'paid' | 'unpaid' | 'overdue' | 'cancelled'

export const INVOICE_STATUS_CONFIG: Record<
    InvoiceStatus,
    { label: string; tone: BadgeTone }
> = {
    paid: { label: 'paid', tone: 'success' },
    unpaid: { label: 'unpaid', tone: 'warning' },
    overdue: { label: 'overdue', tone: 'danger' },
    cancelled: { label: 'cancelled', tone: 'danger' },
}

export type ContractStatus = 'pending' | 'exchanging' | 'uploading' | 'active'

export const CONTRACT_STATUS_CONFIG: Record<
    ContractStatus,
    { label: string; tone: BadgeTone }
> = {
    pending: { label: 'pending', tone: 'warning' },
    exchanging: { label: 'exchanging', tone: 'primary' },
    uploading: { label: 'uploading', tone: 'neutral' },
    active: { label: 'active', tone: 'success' },
}

export interface SearchParams {
    query: string
    scopes?: SearchResultType[]
    account_manager?: string
    status?: string
    created_from?: string
    created_to?: string
    limit?: number
    offset?: number
}

export interface SearchResponse {
    results: SearchResult[]
    total: number
    has_more: boolean
}

export interface SearchSuggestion {
    text: string
    type: SearchResultType
    count: number
}

export interface SearchResultWrapLine {
    label: string
    subtitle?: string
    redirect_to?: string
    created_at: string
    status?: {
        label: string
        tone: BadgeTone
    }
    icon?: React.ReactNode
    email?: string
}
